import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import appVersionManager from '../../utils/appVersion';

const StyledVersion = styled(Box)({
    position: 'relative',
    width: '100%',
    height: '100%',
});

const VersionText = styled(Typography)(({ theme }) => ({
    position: 'absolute',
    bottom: '17px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: theme.palette.figma.white[30],
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '-0.36px',
    textAlign: 'center',
    lineHeight: 'normal',
    width: '320px', // w-80 equivalent
}));

const Version = ({
    version = '0.0.1',
    prefix = 'ver.',
    suffix = '.',
    position = 'bottom', // 'bottom', 'top', 'center'
    showPrefix = true,
    showSuffix = true,
    color = undefined, // Will use theme.palette.figma.white[30] from styled component
    fontSize = '12px',
    fontWeight = 500,
    letterSpacing = '-0.36px',
    useAppVersion = true, // Whether to use app version when available
    sx = {},
    ...props
}) => {
    const [displayVersion, setDisplayVersion] = useState(version);
    const [isInApp, setIsInApp] = useState(false);

    useEffect(() => {
        if (useAppVersion) {
            // Check if we're in the app environment
            const inApp = appVersionManager.isInAppEnvironment();
            setIsInApp(inApp);

            if (inApp) {
                // Try to get app version immediately
                const appVersion = appVersionManager.getFormattedVersion();
                if (appVersion) {
                    setDisplayVersion(appVersion);
                } else {
                    // Wait for app version to be available
                    appVersionManager
                        .waitForVersion(3000)
                        .then(({ version: appVer }) => {
                            const formatted =
                                appVersionManager.getFormattedVersion();
                            if (formatted) {
                                setDisplayVersion(formatted);
                            }
                        })
                        .catch(error => {
                            console.warn('Failed to get app version:', error);
                            // Keep using the default version
                        });
                }

                // Listen for app version updates
                const handleAppVersionUpdate = event => {
                    const formatted = appVersionManager.getFormattedVersion();
                    if (formatted) {
                        setDisplayVersion(formatted);
                    }
                };

                window.addEventListener(
                    'appVersionUpdated',
                    handleAppVersionUpdate
                );

                return () => {
                    window.removeEventListener(
                        'appVersionUpdated',
                        handleAppVersionUpdate
                    );
                };
            }
        }
    }, [useAppVersion, version]);

    const getPositionStyles = () => {
        switch (position) {
            case 'top':
                return {
                    top: '17px',
                    bottom: 'auto',
                };
            case 'center':
                return {
                    top: '50%',
                    bottom: 'auto',
                    transform: 'translate(-50%, -50%)',
                };
            case 'bottom':
            default:
                return {
                    bottom: '10px',
                    top: 'auto',
                };
        }
    };

    const formatVersion = () => {
        // Use displayVersion (which could be app version or default version)
        let formattedVersion = displayVersion;

        // If we're using app version, it's already formatted, so return as is
        if (isInApp && useAppVersion) {
            return formattedVersion;
        }

        // Otherwise, apply prefix and suffix to the version
        if (showPrefix) {
            formattedVersion = `${prefix} ${formattedVersion}`;
        }

        if (showSuffix) {
            formattedVersion = `${formattedVersion}${suffix}`;
        }

        return formattedVersion;
    };

    // Don't render if not in app environment
    if (!isInApp) {
        return null;
    }

    return (
        <StyledVersion sx={sx} {...props}>
            <VersionText
                sx={{
                    ...getPositionStyles(),
                    color: color || undefined, // Use theme default if no custom color provided
                    fontSize,
                    fontWeight,
                    letterSpacing,
                }}
            >
                {formatVersion()}
            </VersionText>
        </StyledVersion>
    );
};

export default Version;
